import { createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import AccountClientRequests from '@api/requests';

import { ApplicationsStateType } from './applications.interfaces';

const initialState: ApplicationsStateType = {
	list: [],
	details: undefined,
	form: undefined,
	currentApplicationActions: {
		downloadApplication: { isAllow: false, errorMessage: undefined },
		downloadTaxForm: { isAllow: false, errorMessage: undefined },
		generateSignLink: { isAllow: false, errorMessage: undefined },
		recertifyW8Ben: { isAllow: false, errorMessage: undefined },
	},
	taxFormListPDF: undefined,
};

export const { actions: applicationsActions, reducer: applicationsReducer } = createSlice({
	name: 'applications',
	initialState,
	reducers: {
		clearGenerateSignLinkData(state) {
			state.currentApplicationActions.generateSignLink = {
				isAllow: false,
				errorMessage: undefined,
			};
		},
	},
	extraReducers: builder =>
		builder
			.addCase(AccountClientRequests.Applications.getApplicationDetails.fulfilled, (state, { payload }) => {
				state.details = payload.application;

				state.list = state.list.map(application =>
					application.id === payload.application.id
						? {
								...application,
								account_number: payload.application.account_number,
								submitted_at: payload.application.submitted_at,
								current_status: payload.application.current_status,
						  }
						: application,
				);
			})
			.addCase(AccountClientRequests.Forms.getFormDetails.fulfilled, (state, { payload }) => {
				state.form = payload.form;
			})
			.addCase(AccountClientRequests.Applications.createApplication.fulfilled, (state, { payload }) => {
				state.list = [...state.list, payload.application].sort((applicationLeft, applicationRight) => {
					if (applicationLeft.account_number === null) return 1;
					if (applicationRight.account_number === null) return -1;
					return 0;
				});
			})
			.addCase(AccountClientRequests.Applications.getApplications.fulfilled, (state, { payload }) => {
				state.list = payload.applications.sort((applicationLeft, applicationRight) => {
					if (applicationLeft.account_number === null) return 1;
					if (applicationRight.account_number === null) return -1;
					return 0;
				});
			})
			.addCase(AccountClientRequests.Applications.saveFileAnswer.fulfilled, (state, { payload, meta }) => {
				state.list = state.list.map(application =>
					application.id === meta.arg.application_id
						? {
								...application,
								documents: application.documents.map(document =>
									document.id === payload.answer.document.id
										? {
												...document,
												current_status: payload.answer.document.current_status,
										  }
										: document,
								),
						  }
						: application,
				);

				if (state.details) {
					state.details.documents = state.details.documents.map(document =>
						document.id === payload.answer.document.id
							? {
									...document,
									current_status: payload.answer.document.current_status,
							  }
							: document,
					);
				}
			})
			.addCase(AccountClientRequests.Applications.submitApplicationForm.fulfilled, (state, { payload }) => {
				state.list = state.list.map(application =>
					application.id === payload.application.id
						? {
								...application,
								account_number: payload.application.account_number,
								submitted_at: payload.application.submitted_at,
								current_status: payload.application.current_status,
						  }
						: application,
				);

				if (state.details) {
					state.details.account_number = payload.application.account_number;
					state.details.submitted_at = payload.application.submitted_at;
					state.details.current_status = payload.application.current_status;
				}
			})
			.addCase(AccountClientRequests.Applications.backgroundCheckApplicationForm.fulfilled, (state, { payload }) => {
				state.list = state.list.map(application =>
					application.id === payload.application.id
						? {
								...application,
								account_number: payload.application.account_number,
								submitted_at: payload.application.submitted_at,
								current_status: payload.application.current_status,
						  }
						: application,
				);

				if (state.details) {
					state.details.account_number = payload.application.account_number;
					state.details.submitted_at = payload.application.submitted_at;
					state.details.current_status = payload.application.current_status;
				}
			})
			.addCase(AccountClientRequests.Applications.verifyDocumentApplicationForm.fulfilled, (state, { payload }) => {
				state.list = state.list.map(application =>
					application.id === payload.application.id
						? {
								...application,
								account_number: payload.application.account_number,
								submitted_at: payload.application.submitted_at,
								current_status: payload.application.current_status,
						  }
						: application,
				);

				if (state.details) {
					state.details.account_number = payload.application.account_number;
					state.details.submitted_at = payload.application.submitted_at;
					state.details.current_status = payload.application.current_status;
				}
			})
			.addCase(AccountClientRequests.Applications.custodianCheckApplicationForm.fulfilled, (state, { payload }) => {
				state.list = state.list.map(application =>
					application.id === payload.application.id
						? {
								...application,
								account_number: payload.application.account_number,
								submitted_at: payload.application.submitted_at,
								current_status: payload.application.current_status,
						  }
						: application,
				);

				if (state.details) {
					state.details.account_number = payload.application.account_number;
					state.details.submitted_at = payload.application.submitted_at;
					state.details.current_status = payload.application.current_status;
				}
			})
			.addCase(AccountClientRequests.Auth.logout.fulfilled, state => {
				state.list = initialState.list;
				state.details = initialState.details;
				state.form = initialState.form;
			})
			.addCase(AccountClientRequests.Applications.generateSignLinkValidation.fulfilled, state => {
				state.currentApplicationActions.generateSignLink = {
					isAllow: true,
				};
			})
			.addCase(AccountClientRequests.Applications.generateSignLinkValidation.rejected, (state, { payload }) => {
				if (payload) {
					const error = payload as AxiosError<{
						status: boolean;
						http_status_message: string;
						messages?: {
							custodian_request_id?: string[];
						};
						error_codes?: Record<string, number[]>;
					}>;

					const messages = error.response?.data?.messages;
					const custodianRequestErrors = messages?.custodian_request_id;

					state.currentApplicationActions.generateSignLink = {
						isAllow: false,
						errorMessage: custodianRequestErrors?.[0],
					};
				}
			})
			.addCase(AccountClientRequests.Applications.downloadApplicationValidation.fulfilled, state => {
				state.currentApplicationActions.downloadApplication = {
					isAllow: true,
				};
			})
			.addCase(AccountClientRequests.Applications.downloadApplicationValidation.rejected, (state, { payload }) => {
				if (payload) {
					const error = payload as AxiosError<{
						status: boolean;
						http_status_message: string;
						messages?: {
							application_id?: string[];
						};
						error_codes?: Record<string, number[]>;
					}>;

					const messages = error.response?.data?.messages;
					const applicationIdErrors = messages?.application_id;

					state.currentApplicationActions.downloadApplication = {
						isAllow: false,
						errorMessage: applicationIdErrors?.[0],
					};
				}
			})
			.addCase(AccountClientRequests.Applications.downloadTaxFormValidation.fulfilled, state => {
				state.currentApplicationActions.downloadTaxForm = {
					isAllow: true,
				};
			})
			.addCase(AccountClientRequests.Applications.downloadTaxFormValidation.rejected, (state, { payload }) => {
				if (payload) {
					const error = payload as AxiosError<{
						status: boolean;
						http_status_message: string;
						messages?: {
							application_id?: string[];
						};
						error_codes?: Record<string, number[]>;
					}>;

					const messages = error.response?.data?.messages;
					const applicationIdErrors = messages?.application_id;

					state.currentApplicationActions.downloadTaxForm = {
						isAllow: false,
						errorMessage: applicationIdErrors?.[0],
					};
				}
			})
			.addCase(AccountClientRequests.Applications.recertifyW8BenFormValidation.fulfilled, state => {
				state.currentApplicationActions.recertifyW8Ben = {
					isAllow: true,
				};
			})
			.addCase(AccountClientRequests.Applications.recertifyW8BenFormValidation.rejected, (state, { payload }) => {
				if (payload) {
					const error = payload as AxiosError<{
						status: boolean;
						http_status_message: string;
						messages?: {
							custodian_request_id?: string[];
						};
						error_codes?: Record<string, number[]>;
					}>;

					const messages = error.response?.data?.messages;
					const custodianRequestErrors = messages?.custodian_request_id;

					state.currentApplicationActions.recertifyW8Ben = {
						isAllow: false,
						errorMessage: custodianRequestErrors?.[0],
					};
				}
			})
			.addCase(AccountClientRequests.Applications.getSigningListPDF.fulfilled, (state, { payload }) => {
				state.taxFormListPDF = payload.request_to_signs;
			})
			.addCase(AccountClientRequests.Applications.agreeCustodianAgreement.fulfilled, (state, { payload, meta }) => {
				if (state.details?.custodian_agreements) {
					state.details.custodian_agreements = state.details.custodian_agreements.map(agreement =>
						agreement.id === meta.arg.custodian_agreement_id ? payload.custodian_agreement : agreement,
					);
				}
			}),
});
